import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Upload, HelpCircle, AlertCircle, FileText, Eye, X, ChevronDown, MessageSquare, Send } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';
import Pagination from '../components/Pagination';

const isEnglishOnly = (str) => {
  if (!str) return true;
  return /^[\x20-\x7E\s]*$/.test(str);
};

const SupportTickets = () => {
  const { toast } = useToast();
  
  // Form states
  const [message, setMessage] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get('page'), 10);
  const initialPage = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
  const sizeParam = parseInt(searchParams.get('size'), 10);
  const initialSize = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 10;

  // Tickets list states
  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const pageParam = parseInt(searchParams.get('page'), 10);
    const p = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
    const sizeParam = parseInt(searchParams.get('size'), 10);
    const s = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 10;
    setCurrentPage(p);
    setPageSize(s);
  }, [searchParams]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (totalPages === 0 || newPage < totalPages)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const newParams = new URLSearchParams(searchParams);
      if (newPage > 0) newParams.set('page', String(newPage + 1));
      else newParams.delete('page');
      if (pageSize !== 10) newParams.set('size', String(pageSize));
      else newParams.delete('size');
      setSearchParams(newParams);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    if (newSize !== 10) newParams.set('size', String(newSize));
    else newParams.delete('size');
    setSearchParams(newParams);
  };

  // Chat Modal States
  const [activeChatTicket, setActiveChatTicket] = useState(null);
  const [modalUserReplyText, setModalUserReplyText] = useState('');
  const [submittingUserReply, setSubmittingUserReply] = useState(false);
  const [expandedAdminReplies, setExpandedAdminReplies] = useState({});

  const toggleAdminReply = (id) => {
    setExpandedAdminReplies(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const fetchMyTickets = async (page = 0, size = pageSize) => {
    setLoading(true);
    try {
      const res = await vendorService.getMySupportTickets(page, size);
      if (res.success && res.data) {
        setTickets(res.data.tickets || []);
        setCurrentPage(res.data.currentPage || 0);
        const calculatedTotalPages = Math.ceil((res.data.totalItems || 0) / size);
        setTotalPages(res.data.totalPages || calculatedTotalPages || 0);
        setTotalItems(res.data.totalItems || 0);
      }
    } catch (err) {
      console.error("Error fetching support tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleSendModalUserReply = async (ticketId) => {
    const text = modalUserReplyText.trim();
    if (!text) {
      toast.warning("Please enter a reply message.");
      return;
    }
    setSubmittingUserReply(true);
    try {
      const res = await vendorService.replySupportTicketUser(ticketId, text);
      if (res.success) {
        toast.success("Reply sent to admin successfully!");
        setModalUserReplyText('');
        setActiveChatTicket(prev => prev ? {
          ...prev,
          status: res.data.status || prev.status,
          replies: res.data.replies || prev.replies,
          user_reply_count: res.data.user_reply_count ?? ((prev.user_reply_count || 0) + 1),
          admin_reply_count: res.data.admin_reply_count ?? (prev.admin_reply_count || 0)
        } : null);
        fetchMyTickets(currentPage, pageSize);
      } else {
        toast.error(res.error || "Failed to send reply.");
      }
    } catch (err) {
      toast.error("An error occurred while sending reply.");
      console.error(err);
    } finally {
      setSubmittingUserReply(false);
    }
  };

  useEffect(() => {
    fetchMyTickets(0, pageSize);
  }, [pageSize]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select a valid image file.");
      return;
    }

    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    }
  };

  const [touched, setTouched] = useState(false);
  const messageError = !message.trim()
    ? "Message details are required."
    : (message.trim().length < 10 
        ? "Message details must be at least 10 characters." 
        : (!isEnglishOnly(message) 
            ? "Please use English characters only (emojis and non-English scripts are not allowed)." 
            : null));

  useEffect(() => {
    if (!isFormOpen) {
      setTouched(false);
    }
  }, [isFormOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (messageError) {
      toast.warning("Please correct the errors in the form.");
      const el = document.getElementsByName('message')[0];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('description', message);
      formData.append('priority', 'MEDIUM');
      if (photo) {
        formData.append('photo', photo);
      }

      const res = await vendorService.createSupportTicket(formData);
      if (res.success) {
        toast.success("Support ticket raised successfully! The admin will review it shortly.");
        setMessage('');
        setTouched(false);
        handleRemovePhoto();
        setIsFormOpen(false);
        fetchMyTickets(0);
      } else {
        toast.error("Failed to submit ticket: " + res.error);
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="flex flex-row items-center justify-between gap-3 border-b border-gray-100 pb-4 sm:pb-5">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Raise Ticket</h2>
          <p className="hidden sm:block text-xs text-gray-400 mt-1 font-medium">Submit operational, listing, profile, or membership queries to the admin.</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="flex-shrink-0 bg-[#2ab391] hover:bg-[#23977a] text-white font-bold py-2 px-3.5 sm:py-2.5 sm:px-4 rounded-xl transition-all shadow-xs cursor-pointer text-center text-xs sm:text-sm flex items-center justify-center gap-1.5 whitespace-nowrap"
        >
          <HelpCircle size={15} />
          <span>Raise Ticket</span>
        </button>
      </div>

      {/* Modal overlay */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xl w-full max-w-lg relative animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setIsFormOpen(false)}
              className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-lg transition-colors cursor-pointer"
              title="Close Form"
            >
              <X size={18} />
            </button>

            <h3 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-1.5">
              <HelpCircle size={18} className="text-[#2ab391]" />
              Raise Ticket
            </h3>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Description Message */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                  Message details <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onBlur={() => setTouched(true)}
                  rows="4"
                  placeholder="Please describe your issue or question in detail..."
                  className={`w-full bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none text-gray-700 placeholder-gray-400 font-medium resize-none leading-relaxed focus:ring-2 ${
                    touched && messageError
                      ? 'border-2 border-red-400 focus:ring-red-300 text-red-700'
                      : 'border-gray-300 focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391]'
                  }`}
                ></textarea>
                {touched && messageError && (
                  <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                    {messageError}
                  </p>
                )}
              </div>

              {/* Screenshot upload zone */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                  Attach Screenshot (optional)
                </label>
                
                {!photoPreview ? (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-[#2ab391] rounded-xl py-6 bg-white cursor-pointer transition-colors group">
                    <Upload className="w-8 h-8 text-gray-400 group-hover:text-[#2ab391] transition-colors mb-2" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-[#2ab391] transition-colors">Click to Upload Image</span>
                    <span className="text-[10px] text-gray-400 mt-1">PNG, JPG, JPEG up to 5MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-white p-2 flex items-center justify-between gap-3 shadow-xs">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <img
                        src={photoPreview}
                        alt="Attachment Preview"
                        className="w-12 h-12 object-cover rounded-md border border-gray-100 flex-shrink-0"
                      />
                      <div className="overflow-hidden">
                        <p className="text-xs font-bold text-gray-700 truncate max-w-[150px]">{photo ? photo.name : 'attachment.jpg'}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Ready to upload</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="p-1.5 hover:bg-rose-50 text-rose-500 rounded-lg border border-transparent hover:border-rose-100 transition-colors cursor-pointer mr-1"
                      title="Remove Photo"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Submit btn */}
              <div className="pt-2 flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#2ab391] hover:bg-[#23977a] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-xl transition-all shadow-xs cursor-pointer text-center text-sm"
                >
                  {submitting ? 'Sending Ticket...' : 'Raise Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tickets List Column - Full Width */}
      <div className="space-y-4">
        <h3 className="text-base font-bold text-gray-700 flex items-center gap-1.5">
          <FileText size={18} className="text-gray-400" />
          Support Tickets History
          <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full ml-1">{totalItems} filed</span>
        </h3>

        {loading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded-2xl py-16 text-center bg-white">
            <AlertCircle size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-500">No support tickets submitted yet.</p>
            <p className="text-xs text-gray-400 mt-1">If you have any queries, feel free to submit them using the button above.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {tickets.map(t => (
              <div
                key={t.id}
                className="bg-white border border-gray-200/90 rounded-2xl p-5 sm:p-6 shadow-xs hover:shadow-md transition-all duration-200 text-left space-y-4"
              >
                {/* Header Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-500 bg-gray-100/80 border border-gray-200/60 px-2.5 py-0.5 rounded-md">
                      #{t.id}
                    </span>

                    <span className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider inline-flex items-center gap-1.5 shadow-2xs ${
                      t.status === 'SOLVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60' :
                      t.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700 border border-blue-200/60' :
                      'bg-amber-50 text-amber-700 border border-amber-200/60'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        t.status === 'SOLVED' ? 'bg-emerald-500' :
                        t.status === 'IN_PROGRESS' ? 'bg-blue-500' :
                        'bg-amber-500'
                      }`}></span>
                      {t.status === 'SOLVED' ? 'Solved' : t.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending'}
                    </span>

                    {t.ticket_type === 'CATEGORY_REQUEST' && (
                      <span className="px-2.5 py-0.5 text-[9px] font-bold rounded-md uppercase bg-teal-50 text-teal-700 border border-teal-200/60">
                        Category Request
                      </span>
                    )}
                  </div>

                  <span className="text-xs text-gray-400 font-medium">
                    Submitted on {new Date(t.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}
                  </span>
                </div>

                {/* Main Body Grid */}
                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                  <div className="space-y-3 flex-1 w-full text-left">
                    {/* Query Details */}
                    {t.ticket_type === 'CATEGORY_REQUEST' ? (
                      <div className="space-y-1.5">
                        <div className="text-xs font-bold text-gray-800 flex items-center gap-1.5 flex-wrap">
                          <span>Requested Category:</span>
                          <span className="px-2.5 py-0.5 text-xs font-bold text-[#2ab391] bg-teal-50 rounded-md border border-teal-100">
                            {t.requested_category}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed max-w-2xl whitespace-pre-wrap font-medium">
                          <span className="font-semibold text-gray-700">Reason: </span>
                          {t.description}
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-700 leading-relaxed max-w-2xl whitespace-pre-wrap font-medium">
                        {t.description}
                      </p>
                    )}

                    {/* Attachment trigger for mobile */}
                    {t.photo_url && (
                      <div className="md:hidden pt-1">
                        <a
                          href={getImageUrl(t.photo_url)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-xs font-bold text-gray-600 hover:text-[#2ab391] transition-colors shadow-2xs"
                        >
                          <Eye size={14} /> View Attachment Screenshot
                        </a>
                      </div>
                    )}

                    {/* Category Request direct inline reply OR Support Ticket See Chats button */}
                    {t.ticket_type === 'CATEGORY_REQUEST' ? (
                      t.admin_reply ? (
                        <div className="mt-3 text-left max-w-2xl">
                          <button
                            type="button"
                            onClick={() => toggleAdminReply(t.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2ab391] hover:text-[#208c71] transition-colors cursor-pointer py-1"
                          >
                            <svg className="w-3.5 h-3.5 text-[#2ab391]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                            </svg>
                            <span>Admin Response</span>
                            <ChevronDown size={14} className={`transition-transform duration-200 ${expandedAdminReplies[t.id] ? 'rotate-180' : ''}`} />
                          </button>

                          {expandedAdminReplies[t.id] && (
                            <div className="mt-2 p-3.5 sm:p-4 bg-[#eaf7f4]/90 border border-[#2ab391]/30 rounded-xl text-xs space-y-1.5 shadow-2xs animate-in fade-in zoom-in-95 duration-150">
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#2ab391]/15 pb-2">
                                <span className="font-bold text-[#2ab391] text-xs">
                                  Admin Reply Details
                                </span>
                                {t.replied_at && (
                                  <span className="text-[10px] text-gray-500 font-semibold bg-white/90 px-2 py-0.5 rounded-full border border-teal-100">
                                    Replied on {new Date(t.replied_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-800 font-medium leading-relaxed whitespace-pre-wrap pt-0.5">
                                {t.admin_reply}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="pt-2 text-xs text-amber-700 font-semibold bg-amber-50/80 border border-amber-200/60 p-2.5 rounded-xl flex items-center gap-2 max-w-md">
                          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                          <span>Awaiting Admin Response...</span>
                        </div>
                      )
                    ) : (
                      /* Support Ticket: See Chats button */
                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveChatTicket(t);
                            setModalUserReplyText('');
                          }}
                          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-[#eaf7f4] hover:bg-[#d4f1ea] border border-[#2ab391]/30 text-xs font-bold text-[#2ab391] transition-all cursor-pointer shadow-2xs group"
                        >
                          <MessageSquare size={14} className="text-[#2ab391] group-hover:scale-110 transition-transform" />
                          <span>See Chats</span>
                          {((t.replies && t.replies.length > 0) || t.admin_reply) && (
                            <span className="bg-[#2ab391] text-white text-[10px] font-extrabold px-1.5 py-0.2 rounded-full">
                              {t.replies ? t.replies.length : 1}
                            </span>
                          )}
                        </button>

                        <span className="text-[11px] text-gray-400 font-medium">
                          {t.status === 'SOLVED' ? 'Ticket Resolved' : 'Click to view conversation'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Attachment Desktop thumbnail */}
                  {t.photo_url && (
                    <a
                      href={getImageUrl(t.photo_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="hidden md:flex flex-shrink-0 relative group rounded-xl overflow-hidden border border-gray-200 p-1 bg-white hover:border-[#2ab391] transition-all items-center justify-center shadow-2xs"
                    >
                      <img
                        src={getImageUrl(t.photo_url)}
                        alt="Screenshot"
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                        <Eye size={14} />
                      </div>
                    </a>
                  )}
                </div>
              </div>
            ))}

          {/* Pagination Controls */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            className="mt-6"
          />
          </div>
        )}
      </div>

      {/* Chat Modal Overlay - WhatsApp Style (Compact Width & Mobile Margins) */}
      {activeChatTicket && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md sm:max-w-lg overflow-hidden flex flex-col h-[85vh] sm:h-[80vh] max-h-[90vh] border border-gray-100/90">
            {/* WhatsApp Teal Header */}
            <div className="p-3.5 sm:p-4 bg-[#075e54] text-white flex items-center justify-between shadow-md shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center border border-white/20 text-white flex-shrink-0">
                  <MessageSquare size={18} />
                </div>
                <div className="min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold tracking-wide">Ticket #{activeChatTicket.id}</span>
                    <span className={`px-2 py-0.2 text-[9px] font-bold rounded-full uppercase tracking-wider ${
                      activeChatTicket.status === 'SOLVED' ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-400/30' :
                      activeChatTicket.status === 'IN_PROGRESS' ? 'bg-blue-500/30 text-blue-200 border border-blue-400/30' :
                      'bg-amber-500/30 text-amber-200 border border-amber-400/30'
                    }`}>
                      {activeChatTicket.status === 'SOLVED' ? 'Solved' : activeChatTicket.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-[11px] text-teal-100 font-medium truncate max-w-[200px] sm:max-w-xs">
                    {activeChatTicket.ticket_type === 'CATEGORY_REQUEST' ? `Category Request: ${activeChatTicket.requested_category}` : activeChatTicket.description}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveChatTicket(null)}
                className="w-8 h-8 rounded-full hover:bg-white/15 text-white flex items-center justify-center transition-colors cursor-pointer flex-shrink-0 ml-2"
                title="Close Chat"
              >
                <X size={18} />
              </button>
            </div>

            {/* Conversation Thread - WhatsApp Chat Wallpaper Background */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-[#efeae2]">
              {/* Date Pill / Original Query Badge */}
              <div className="flex justify-center my-1">
                <div className="bg-white/90 backdrop-blur-xs text-gray-600 text-[10px] sm:text-[11px] font-semibold px-3 py-1 rounded-full shadow-2xs border border-gray-200/50 text-center max-w-[90%]">
                  Original Query • {new Date(activeChatTicket.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' })}
                </div>
              </div>

              {/* Original User Query Bubble */}
              <div className="flex flex-col items-end my-1">
                <div className="bg-[#d9fdd3] text-gray-900 border border-[#bbf2b3]/60 rounded-xl rounded-tr-none p-2.5 sm:p-3 text-xs max-w-[85%] sm:max-w-[78%] shadow-2xs text-left">
                  <p className="font-semibold text-[10px] text-teal-800 uppercase tracking-wider mb-0.5">Your Initial Request</p>
                  <p className="font-medium text-gray-900 leading-relaxed whitespace-pre-wrap">
                    {activeChatTicket.ticket_type === 'CATEGORY_REQUEST' ? (
                      <>Category: <strong>{activeChatTicket.requested_category}</strong> — {activeChatTicket.description}</>
                    ) : (
                      activeChatTicket.description
                    )}
                  </p>
                  <div className="flex items-center justify-end gap-1 text-[10px] text-gray-500 mt-1 font-mono">
                    <span>{new Date(activeChatTicket.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="text-[#53bdeb] font-bold text-[11px]">✓✓</span>
                  </div>
                </div>
              </div>

              {/* Replies Thread */}
              {(!activeChatTicket.replies || activeChatTicket.replies.length === 0) && !activeChatTicket.admin_reply ? (
                <div className="py-8 text-center text-xs text-gray-400 space-y-2">
                  <div className="bg-white/80 inline-block px-4 py-2 rounded-xl border border-gray-200/50 shadow-2xs text-gray-500 font-medium">
                    Awaiting admin reply...
                  </div>
                </div>
              ) : activeChatTicket.replies && activeChatTicket.replies.length > 0 ? (
                activeChatTicket.replies.map((r, idx) => (
                  <div
                    key={r.id || idx}
                    className={`flex flex-col ${r.sender_type === 'ADMIN' ? 'items-start' : 'items-end'} my-1.5`}
                  >
                    <div
                      className={`p-2.5 sm:p-3 text-xs space-y-1 max-w-[85%] sm:max-w-[78%] shadow-2xs text-left ${
                        r.sender_type === 'ADMIN'
                          ? 'bg-white border border-gray-200/70 text-gray-900 rounded-xl rounded-tl-none'
                          : 'bg-[#d9fdd3] border border-[#bbf2b3]/60 text-gray-900 rounded-xl rounded-tr-none'
                      }`}
                    >
                      {r.sender_type === 'ADMIN' && (
                        <p className="font-bold text-[#075e54] text-[11px]">Admin</p>
                      )}
                      <p className="font-medium text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {r.message}
                      </p>
                      <div className="flex items-center justify-end gap-1 text-[10px] text-gray-500 mt-1 font-mono">
                        <span>{new Date(r.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        {r.sender_type !== 'ADMIN' && (
                          <span className="text-[#53bdeb] font-bold text-[11px]">✓✓</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : activeChatTicket.admin_reply ? (
                <div className="flex flex-col items-start my-1.5">
                  <div className="bg-white border border-gray-200/70 text-gray-900 rounded-xl rounded-tl-none p-2.5 sm:p-3 text-xs max-w-[85%] sm:max-w-[78%] shadow-2xs text-left">
                    <p className="font-bold text-[#075e54] text-[11px]">Admin</p>
                    <p className="font-medium text-gray-900 leading-relaxed whitespace-pre-wrap">
                      {activeChatTicket.admin_reply}
                    </p>
                    <div className="flex items-center justify-end text-[10px] text-gray-500 mt-1 font-mono">
                      <span>{activeChatTicket.replied_at ? new Date(activeChatTicket.replied_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* WhatsApp Input Bar */}
            <div className="p-2.5 sm:p-3 bg-[#f0f2f5] border-t border-gray-200/80 shrink-0">
              {activeChatTicket.ticket_type === 'CATEGORY_REQUEST' ? (
                <p className="text-xs text-center text-gray-500 font-semibold py-1">
                  Category requests allow 1 admin response.
                </p>
              ) : activeChatTicket.status === 'SOLVED' ? (
                <p className="text-xs text-center text-emerald-700 font-bold bg-emerald-50 border border-emerald-200/60 p-2 rounded-xl">
                  This ticket is marked as solved. No further replies allowed.
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={modalUserReplyText}
                    onChange={(e) => setModalUserReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (modalUserReplyText.trim() && !submittingUserReply) {
                          handleSendModalUserReply(activeChatTicket.id);
                        }
                      }
                    }}
                    placeholder="Type a message..."
                    className="flex-1 bg-white rounded-full px-4 py-2 sm:py-2.5 border border-gray-200 text-xs sm:text-sm font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#00a884] focus:border-[#00a884] shadow-2xs placeholder-gray-400"
                  />
                  <button
                    type="button"
                    disabled={submittingUserReply || !modalUserReplyText.trim()}
                    onClick={() => handleSendModalUserReply(activeChatTicket.id)}
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#00a884] hover:bg-[#008f70] disabled:bg-gray-300 text-white flex items-center justify-center transition-all cursor-pointer shadow-md shrink-0 active:scale-95"
                    title="Send Message"
                  >
                    <Send size={16} className="translate-x-[1px]" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
